'use client';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { MODEL_VERSION_SETTINGS_MAP, VIDU_WELCOME_STORAGE_KEY } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { useRouter } from '@/i18n/routing';
import { Q4_DIALOG_TITLE_IMAGES, VIDEO_RESOURCES } from './constants';
import { HeroIndicator } from './HeroIndicator';
import { HeroWordmark, type HeroWordmarkVariant } from './HeroWordmark';
import { PlaceholdersAndVanishInput } from './PlaceholdersAndVanishInput';
import { SequentialVideoCarousel } from './SequentialVideoCarousel';

interface NavigatorWithConnection extends Navigator {
  connection?: {
    saveData?: boolean;
  };
}

/** 各视频对应的字标徽章：前两个视频 → Q3，第 3 个 → S。 */
const WORDMARK_BY_NAME: Record<string, HeroWordmarkVariant> = {
  heroQ4: 'q3',
  hero: 'q3',
  hero2: 's1',
};

/** 各视频对应的副标题文案 key。 */
const TAGLINE_KEY_BY_NAME: Record<string, string> = {
  heroQ4: 'taglineQ4',
  hero: 'taglineQ3',
  hero2: 'taglineS1',
};

export const getTaglineClassName = (locale: string): string =>
  [
    'text-[16px] text-white/80 leading-[30px] md:text-[10px] md:leading-[22px]',
    locale === 'zh' ? 'tracking-[0.5em] md:tracking-[0.4em]' : '',
  ]
    .filter(Boolean)
    .join(' ');

export const HeroSection = () => {
  const [announcementHeight, setAnnouncementHeight] = useState(0);
  const [currentVideoNumber, setCurrentVideoNumber] = useState(0);
  const [canLoadHeroVideo, setCanLoadHeroVideo] = useState(false);
  const [isInViewport, setIsInViewport] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const router = useRouter();
  const locale = useLocale();
  const { updateFormParams, updateModelVersion, setPendingClawInput } = useStore((state) => ({
    updateFormParams: state.updateFormParams,
    updateModelVersion: state.updateModelVersion,
    setPendingClawInput: state.setPendingClawInput,
  }));
  const t = useTranslations();
  const currentVideoResource = VIDEO_RESOURCES[currentVideoNumber];
  const currentTitleImage =
    currentVideoResource.name === 'heroQ4'
      ? Q4_DIALOG_TITLE_IMAGES[locale === 'zh' ? 'zh' : 'en']
      : currentVideoResource.titleImage;

  // 视差：首屏被推上去时，视频层比容器移动得慢（跟随 0.35 倍），文字层保持跟手
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  const handleVideoEnded = () => {
    setCurrentVideoNumber((prev) => (prev + 1) % VIDEO_RESOURCES.length);
  };

  const handleSelectVideo = (index: number) => {
    setCurrentVideoNumber(index);
  };

  // 横幅显示、关闭或随窗口缩放时，视频始终填满首屏剩余空间。
  useEffect(() => {
    const topBar = document.getElementById('landing-top-bar');
    if (!topBar) return;

    const updateHeight = () => setAnnouncementHeight(topBar.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(topBar);
    return () => observer.disconnect();
  }, []);

  const initImg2VideoFormParams = (realText?: string) => {
    const currentVideoResource = VIDEO_RESOURCES[currentVideoNumber];
    const { modelVersion, settings, poster } = currentVideoResource;

    updateFormParams(
      'img2video',
      'textPrompt',
      realText || t(`home3.heroSection.prompts.${currentVideoResource.name}`),
    );
    updateFormParams('img2video', 'imagePrompts', [
      {
        id: uuidv4(),
        status: 'done',
        prompt: {
          sourceImage: poster,
          sourceImagePreview: poster,
          selectedImage: poster,
          selectedImagePreview: poster,
        },
      },
    ]);
    if (modelVersion) {
      updateFormParams('img2video', MODEL_VERSION_SETTINGS_MAP[modelVersion], settings);
      updateModelVersion('img2video', modelVersion);
    }
  };

  const handleClickTryVidu = (text?: string) => {
    const realText = text?.trim();
    if (realText) {
      safeStorage.setItem(VIDU_WELCOME_STORAGE_KEY, realText);
    }
    const currentVideoResource = VIDEO_RESOURCES[currentVideoNumber];

    reportEvent('element_click', {
      element_name: 'landing - create',
      case_name: currentVideoResource.name,
      case_index: currentVideoNumber,
    });

    if (currentVideoResource.isClaw) {
      const prompt = realText || t(`home3.heroSection.prompts.${currentVideoResource.name}`) || '';
      setPendingClawInput({
        prompt,
        referenceImageUrl: currentVideoResource.clawWithReferenceImage ? currentVideoResource.poster : undefined,
      });
      router.push('/claw');
      return;
    }

    initImg2VideoFormParams(realText);
    router.push('/create/img2video');
  };

  // hydration 后下一帧再拉 MP4：首屏仍靠 page preload 的 poster，避免与同步渲染抢主线程
  useEffect(() => {
    // 每次进入首页等概率选择 Q4 或 S，在加载视频前确定起播项，避免 hydration 不一致。
    setCurrentVideoNumber(Math.random() < 0.5 ? 0 : 1);

    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection?.saveData) return;

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (!cancelled) {
        setCanLoadHeroVideo(true);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, []);

  // 只监听组件滚出视口的情况
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setIsInViewport(entries[0].isIntersecting);
      },
      { threshold: 0.05 },
    );

    observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={sectionRef}
      className="relative z-10 flex min-h-0 flex-col items-center justify-center overflow-hidden sm:justify-end md:justify-end"
      style={{ height: `calc(100svh - ${announcementHeight}px)` }}
    >
      {/* 视频容器：随滚动做视差位移（放大一点避免位移时露出底部边缘） */}
      <motion.div
        className="absolute inset-0 h-full w-full overflow-hidden"
        style={prefersReducedMotion ? undefined : { y: mediaY, scale: 1.12 }}
      >
        <SequentialVideoCarousel
          resources={VIDEO_RESOURCES}
          currentIndex={currentVideoNumber}
          enabled={canLoadHeroVideo && isInViewport}
          onEnded={handleVideoEnded}
        />

        {/* 12% 黑色蒙版（整屏均匀压暗）——放在视频层内，跟随视频一起位移缩放 */}
        <div className="pointer-events-none absolute inset-0 bg-black/[0.12]" />

        {/* 底部渐变蒙版：黑色，从上到下 0 → 40%，高度为首屏 30%，底部对齐——同样跟随视频 */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-b from-transparent to-black/40" />
      </motion.div>

      {/* 水平居中 + 底部对齐：字标 + 副标题 + 提示词输入框（输入框在文字下方），随滚动做轻微视差 */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-end px-6 pb-[128px] text-center md:pb-[104px]"
        style={prefersReducedMotion ? undefined : { y: contentY }}
      >
        {currentTitleImage ? (
          <motion.img
            key={currentVideoNumber}
            src={currentTitleImage}
            alt="Vidu Q4"
            className="h-[160px] w-auto sm:h-[80px] md:h-[120px] lg:h-[160px]"
            initial={prefersReducedMotion ? undefined : { clipPath: 'inset(0 100% 0 0)' }}
            animate={{ clipPath: 'inset(0 0% 0 0)' }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />
        ) : (
          <HeroWordmark
            key={currentVideoNumber}
            variant={WORDMARK_BY_NAME[VIDEO_RESOURCES[currentVideoNumber].name] ?? 'q3'}
            className="h-[80px] sm:h-[32px] md:h-[38px] lg:h-[50px]"
          />
        )}
        {/* 参照 NIO：带字距的副标题标语（随视频切换） */}
        {currentVideoResource.name !== 'heroQ4' && (
          <p className={getTaglineClassName(locale)}>
            {t(`home3.heroSection.${TAGLINE_KEY_BY_NAME[currentVideoResource.name] ?? 'taglineQ3'}`)}
          </p>
        )}
        {/* 第一个视频：提示词输入框（描边样式）；第二个视频：立即体验按钮。移动端均隐藏 */}
        {VIDEO_RESOURCES[currentVideoNumber].name === 'heroQ4' ? (
          <div className="mt-8 h-[60px] w-[640px] md:hidden lg:w-[560px]">
            <PlaceholdersAndVanishInput
              variant="outline"
              placeholder={t(`home3.heroSection.prompts.${VIDEO_RESOURCES[currentVideoNumber].name}`)}
              buttonText={t('home3.heroSection.createButton')}
              onSubmit={handleClickTryVidu}
            />
          </div>
        ) : (
          <ClientLink
            href="/vidu-stream"
            className="mt-8 box-border flex h-[60px] w-[200px] cursor-pointer items-center justify-center rounded-full border border-white/40 bg-transparent font-semibold text-[16px] text-white transition hover:bg-white/10 md:hidden"
            aria-label="trynow"
            onClick={() => {
              reportEvent('element_click', {
                element_name: 'landing - trynow',
                case_name: VIDEO_RESOURCES[currentVideoNumber].name,
                case_index: currentVideoNumber,
              });
            }}
          >
            {t('home3.heroSection.navHome')}
          </ClientLink>
        )}
      </motion.div>

      {/* 右下角：横条轮播指示器（多视频时才显示） */}
      {VIDEO_RESOURCES.length > 1 && (
        <div className="absolute right-0 bottom-0 flex justify-end px-[88px] pb-[88px] md:px-16 md:pb-16">
          <HeroIndicator
            keys={VIDEO_RESOURCES.map((r) => r.name)}
            activeIndex={currentVideoNumber}
            onSelect={handleSelectVideo}
          />
        </div>
      )}

      {/* 底部居中：向下滚动提示 */}
      <div className="-translate-x-1/2 pointer-events-none absolute bottom-6 left-1/2 flex flex-col items-center text-white/70 md:bottom-4">
        <BaseLineDown className="size-6 animate-scroll-down-hint" />
      </div>
    </div>
  );
};
